import { useAccountsStore as useCoreAccountsStore } from '@flows.cash/core';
import { Account } from '@flows.cash/types';
import { create } from 'zustand';
import { toast } from 'sonner';
import { useAuthStore } from './authStore';
import { useEffect } from 'react';

interface WebAccountsState {
  accounts: Account[];
  loading: boolean;
  error: string | null;
  addAccount: (account: Omit<Account, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => Promise<string | null>;
  updateAccount: (id: string, account: Partial<Account>) => Promise<void>;
  deleteAccount: (id: string) => Promise<void>;
  refreshAccountBalance: (accountId: string) => Promise<number | null>;
  refreshAllBalances: () => Promise<void>;
  fetchAccounts: (userId: string) => Promise<Account[]>;
}

// Create a web-specific store that syncs with the core store
export const useAccountsStore = create<WebAccountsState>((set, get) => ({
  accounts: [],
  loading: false,
  error: null,

  fetchAccounts: async (userId: string) => {
    if (!userId) {
      toast.error('You must be logged in to fetch accounts');
      return [];
    }
    
    try {
      set({ loading: true, error: null });
      
      // Use the core store to fetch accounts
      const accounts = await useCoreAccountsStore.getState().fetchAccounts(userId);
      
      // Update our local state with the accounts from the core store
      set({ 
        accounts, 
        loading: false,
        error: null
      });
      
      return accounts;
    } catch (error) {
      console.error('Error fetching accounts:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch accounts',
        loading: false
      });
      toast.error(error instanceof Error ? error.message : 'Failed to fetch accounts');
      return [];
    }
  },

  addAccount: async (account) => {
    console.log('🔍 AccountsStore: addAccount called with account:', account);
    
    const userId = useAuthStore.getState().user?.id;
    console.log('👤 AccountsStore: User ID:', userId);
    
    if (!userId) {
      console.error('❌ AccountsStore: No user ID found');
      toast.error('You must be logged in to add an account');
      return null;
    }
    
    try {
      console.log('🔄 AccountsStore: Calling core addAccount with userId:', userId);
      const accountId = await useCoreAccountsStore.getState().addAccount(userId, account);
      console.log('✅ AccountsStore: Core addAccount completed with ID:', accountId);
      
      if (accountId) {
        // Update our local state with the latest accounts from the core store
        const accounts = useCoreAccountsStore.getState().accounts;
        console.log('📊 AccountsStore: Updated accounts from core store:', accounts.length);
        set({ accounts });
        
        console.log('🔔 AccountsStore: Showing success toast');
        toast.success(`Account "${account.name}" created successfully`);
        return accountId;
      } else {
        console.error('❌ AccountsStore: No account ID returned from core addAccount');
        toast.error('Failed to create account');
        return null;
      }
    } catch (error) {
      console.error('❌ AccountsStore: Error adding account:', error);
      if (error instanceof Error) {
        console.error('❌ AccountsStore: Error message:', error.message);
        console.error('❌ AccountsStore: Error stack:', error.stack);
      }
      toast.error(error instanceof Error ? error.message : 'Failed to create account');
      return null;
    }
  },
  
  updateAccount: async (id, account) => {
    console.log('🔍 AccountsStore: updateAccount called with id:', id);
    console.log('🔍 AccountsStore: updateAccount called with account:', account);
    
    const userId = useAuthStore.getState().user?.id;
    console.log('👤 AccountsStore: User ID:', userId);
    
    if (!userId) {
      console.error('❌ AccountsStore: No user ID found');
      toast.error('You must be logged in to update an account');
      return;
    }

    try {
      console.log('🔄 AccountsStore: Calling core updateAccount with userId:', userId, 'id:', id);
      await useCoreAccountsStore.getState().updateAccount(userId, id, account);
      console.log('✅ AccountsStore: Core updateAccount completed successfully');
      
      // Update our local state with the latest accounts from the core store
      const accounts = useCoreAccountsStore.getState().accounts;
      console.log('📊 AccountsStore: Updated accounts from core store:', accounts.length);
      set({ accounts });
      
      console.log('🔔 AccountsStore: Showing success toast');
      toast.success(`Account "${account.name || 'account'}" updated successfully`);
    } catch (error) {
      console.error('❌ AccountsStore: Error updating account:', error);
      if (error instanceof Error) {
        console.error('❌ AccountsStore: Error message:', error.message);
        console.error('❌ AccountsStore: Error stack:', error.stack);
      }
      toast.error(error instanceof Error ? error.message : 'Failed to update account');
    }
  },
  
  deleteAccount: async (id) => {
    const userId = useAuthStore.getState().user?.id;
    
    if (!userId) {
      toast.error('You must be logged in to delete an account');
        return;
      }

    try {
      // Get account name before deletion for the success message
      const accountName = useCoreAccountsStore.getState().accounts.find(a => a.id === id)?.name || 'account';
      
      await useCoreAccountsStore.getState().deleteAccount(userId, id);
      
      // Update our local state with the latest accounts from the core store
      const accounts = useCoreAccountsStore.getState().accounts;
      set({ accounts });
      
      toast.success(`Account "${accountName}" deleted successfully`);
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete account');
    }
  },
  
  refreshAccountBalance: async (accountId) => {
    const userId = useAuthStore.getState().user?.id;
    
    if (!userId) {
      console.error('No user ID found for refreshing account balance');
      return null;
    }

    try {
      const balance = await useCoreAccountsStore.getState().refreshAccountBalance(userId, accountId);
      
      // Update our local state with the latest accounts from the core store
      const accounts = useCoreAccountsStore.getState().accounts;
      set({ accounts });
      
      return balance;
    } catch (error) {
      console.error('Error refreshing account balance:', error);
      return null;
    }
  },

  refreshAllBalances: async () => {
    const userId = useAuthStore.getState().user?.id;
    
    if (!userId) {
      console.error('No user ID found for refreshing account balances');
      return;
    }
      
    try {
      await useCoreAccountsStore.getState().refreshAllBalances(userId);
      
      // Update our local state with the latest accounts from the core store
      const accounts = useCoreAccountsStore.getState().accounts;
      set({ accounts });
      
      // Removed success toast - silent operation
    } catch (error) {
      console.error('Error refreshing all balances:', error);
      // Only show error toast for critical failures
      if (error instanceof Error && error.message.includes('auth')) {
        toast.error('Authentication error while refreshing balances');
      }
    }
  }
}));

// Create a hook that syncs the web store with the core store
export const useAccounts = () => {
  const { accounts: coreAccounts, loading: coreLoading, error: coreError } = useCoreAccountsStore();
  const { accounts, loading, error, fetchAccounts } = useAccountsStore();
  const { user } = useAuthStore();
  
  // Sync the web store with the core store whenever the core store changes
  useEffect(() => {
    if (coreAccounts && coreAccounts.length > 0 && (!accounts || accounts.length === 0)) {
      console.log('Syncing web accounts store with core store:', coreAccounts.length, 'accounts');
      useAccountsStore.setState({ accounts: coreAccounts, loading: coreLoading, error: coreError });
    }
  }, [coreAccounts, coreLoading, coreError, accounts]);
  
  // Fetch accounts if we have a user but no accounts
  useEffect(() => {
    if (user?.id && (!accounts || accounts.length === 0) && !loading) {
      console.log('Auto-fetching accounts for user:', user.id);
      fetchAccounts(user.id);
    }
  }, [user?.id, accounts, loading, fetchAccounts]);
  
  return { 
    accounts: accounts && accounts.length > 0 ? accounts : coreAccounts, 
    loading: loading || coreLoading, 
    error: error || coreError 
  };
};

// Export the core store's functions for direct access
export const { clearAccounts } = useCoreAccountsStore.getState();